import React from 'react';
import {
    Search, ChevronLeft, ChevronRight, ExternalLink, Trash2, Loader
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

export default function JobListingsTab({
    jobs,
    loading,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    setDebouncedFilters,
    dateMode,
    setDateMode,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    page,
    setPage,
    totalPages,
    total,
    onDeleteJob,
}) {
    const { isDarkMode } = useTheme();

    return (
        <div className="space-y-6">
            {/* Search and Filters */}
            <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                <div className="mb-4">
                    <div className="relative">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} size={20} />
                        <input
                            type="text"
                            placeholder="Search jobs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300'}`}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <input
                        type="text"
                        placeholder="Title"
                        value={filters.title}
                        onChange={(e) => setFilters(prev => ({ ...prev, title: e.target.value }))}
                        className={`px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                    />
                    <input
                        type="text"
                        placeholder="Company"
                        value={filters.company}
                        onChange={(e) => setFilters(prev => ({ ...prev, company: e.target.value }))}
                        className={`px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                    />
                    <input
                        type="text"
                        placeholder="Location"
                        value={filters.location}
                        onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                        className={`px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                    />
                    <input
                        type="text"
                        placeholder="Industry"
                        value={filters.industry}
                        onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                        className={`px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                    />
                    <select
                        value={filters.timeType}
                        onChange={(e) => {
                            const nextFilters = { ...filters, timeType: e.target.value };
                            setFilters(nextFilters);
                            setDebouncedFilters(nextFilters);
                            setPage(1);
                        }}
                        className={`px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                    >
                        <option value="">All Types</option>
                        <option value="Full-Time">Full-Time</option>
                        <option value="Part-Time">Part-Time</option>
                        <option value="Contract">Contract</option>
                        <option value="Internship">Internship</option>
                    </select>
                    <select
                        value={filters.status ?? 'active'}
                        onChange={(e) => {
                            const nextFilters = { ...filters, status: e.target.value };
                            setFilters(nextFilters);
                            setDebouncedFilters(nextFilters);
                            setPage(1);
                        }}
                        className={`px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                    >
                        <option value="active">Active Only</option>
                        <option value="retired">Retired Only</option>
                        <option value="all">All Listings</option>
                    </select>
                </div>

                {/* Date range filter */}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Date range:</span>
                    {['all', 'custom'].map((mode) => (
                        <button
                            key={mode}
                            onClick={() => {
                                setDateMode(mode);
                                if (mode === 'custom') {
                                    const d = new Date();
                                    d.setDate(d.getDate() - 21);
                                    setDateFrom(d.toISOString().split('T')[0]);
                                    setDateTo(new Date().toISOString().split('T')[0]);
                                }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                                dateMode === mode
                                    ? 'bg-primary-600 text-white border-primary-600'
                                    : isDarkMode
                                        ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                        : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            {mode === 'custom' ? 'Custom Range' : 'All'}
                        </button>
                    ))}
                    {dateMode === 'custom' && (
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className={`px-3 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                            />
                            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>to</span>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className={`px-3 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Jobs Table */}
            {loading ? (
                <div className={`rounded-2xl border shadow-sm p-12 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <Loader className="animate-spin mx-auto text-primary-600 mb-4" size={48} />
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Loading jobs...</p>
                </div>
            ) : (
                <div className={`rounded-2xl border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={`border-b ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                <tr>
                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Title</th>
                                    <th className={`px-8 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Company</th>
                                    <th className={`px-8 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Location</th>
                                    <th className={`px-8 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Type</th>
                                    <th className={`px-8 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Status</th>
                                    <th className={`px-8 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Posted</th>
                                    <th className={`px-8 py-3 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                                {jobs.map(job => (
                                    <tr key={job.JobListingID} className={`${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} ${!job.isActive ? 'opacity-60' : ''}`}>
                                        <td className="px-6 py-4">
                                            <div className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{job.JobTitle}</div>
                                            {job.role_name && (
                                                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{job.role_name}</div>
                                            )}
                                        </td>
                                        <td className={`px-6 py-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{job.CompanyName || 'N/A'}</td>
                                        <td className={`px-6 py-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{job.JobLocation || 'N/A'}</td>
                                        <td className="justify-center py-4">
                                            <span className="flex m-2 justify-center px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs">
                                                {job.TimeType || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {job.RetiredOn ? (
                                                <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded text-xs">
                                                    Retired
                                                </span>
                                            ) : job.isActive ? (
                                                <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                            {job.JobPostedOn ? new Date(job.JobPostedOn).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => onDeleteJob(job.JobListingID)}
                                                    className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                                <a
                                                    href={job.JobLinkURL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-600 text-slate-300' : 'hover:bg-slate-100'}`}
                                                >
                                                    <ExternalLink size={16} />
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className={`px-6 py-4 border-t flex items-center justify-between ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                        <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                            Showing {jobs.length} of {total} jobs
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className={`p-2 border rounded-lg disabled:opacity-50 ${isDarkMode ? 'border-slate-600 hover:bg-slate-700 text-slate-300' : 'border-slate-300 hover:bg-slate-50'}`}
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <span className={`px-4 py-2 text-sm ${isDarkMode ? 'text-slate-300' : ''}`}>
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className={`p-2 border rounded-lg disabled:opacity-50 ${isDarkMode ? 'border-slate-600 hover:bg-slate-700 text-slate-300' : 'border-slate-300 hover:bg-slate-50'}`}
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}